import { useState } from 'react';
import { ResponsiveOverlay } from '@/components/ui/ResponsiveOverlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useProducerTeam } from '@/hooks/useProducerTeam';
import type { ProducerRole, InviteMemberPayload } from '@/types/team';

interface InviteMemberSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  producerId: string;
}

export function InviteMemberSheet({ open, onOpenChange, producerId }: InviteMemberSheetProps) {
  const { inviteMember } = useProducerTeam(producerId);
  
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ProducerRole>('editor');
  const [isPublic, setIsPublic] = useState(false);
  const [publicName, setPublicName] = useState('');
  const [title, setTitle] = useState('');
  const [bio, setBio] = useState('');
  const [isSending, setIsSending] = useState(false);

  const resetForm = () => {
    setEmail('');
    setRole('editor');
    setIsPublic(false);
    setPublicName('');
    setTitle('');
    setBio('');
  };

  const handleSubmit = async () => {
    if (!email.trim()) {
      toast.error('Ange en e-postadress');
      return;
    }

    if (!email.includes('@') || !email.includes('.')) {
      toast.error('Ange en giltig e-postadress');
      return;
    }

    setIsSending(true);
    try {
      const payload: InviteMemberPayload = {
        email: email.trim().toLowerCase(),
        role,
        isPublic,
        ...(isPublic && {
          publicName: publicName.trim() || undefined,
          title: title.trim() || undefined,
          bio: bio.trim() || undefined,
        }),
      };

      await inviteMember(payload);
      resetForm();
      onOpenChange(false);
    } catch {
      // Error toast is shown by the hook
    } finally {
      setIsSending(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  return (
    <ResponsiveOverlay
      open={open}
      onOpenChange={handleOpenChange}
      title="Bjud in person"
      description="Skicka en inbjudan till någon som ska hjälpa till med gården."
      showHeader
      variant="sidepanel"
    >
      <div className="space-y-6 py-6">
        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email">E-postadress *</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="namn@exempel.se"
            autoComplete="email"
          />
        </div>

        {/* Role */}
        <div className="space-y-2">
          <Label htmlFor="role">Roll för denna gård</Label>
          <Select value={role} onValueChange={(v) => setRole(v as ProducerRole)}>
            <SelectTrigger id="role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">Medvärd – Full åtkomst</SelectItem>
              <SelectItem value="editor">Redaktör – Redigering</SelectItem>
              <SelectItem value="viewer">Läsare – Endast visning</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            {role === 'admin' && 'Kan hantera allt utom ta bort ägaren.'}
            {role === 'editor' && 'Kan redigera gårdsinformation, bilder och aktiviteter.'}
            {role === 'staff' && 'Kan hantera produkter och ordrar.'}
            {role === 'viewer' && 'Kan se information men inte ändra något.'}
          </p>
        </div>

        <Separator />

        {/* Public profile toggle */}
        <div className="flex items-center justify-between">
          <div>
            <Label htmlFor="isPublic" className="font-medium">Visa personen publikt</Label>
            <p className="text-sm text-muted-foreground">
              Visa på gårdssidan under "Människorna bakom gården".
            </p>
          </div>
          <Switch
            id="isPublic"
            checked={isPublic}
            onCheckedChange={setIsPublic}
          />
        </div>

        {/* Public profile fields (conditional) */}
        {isPublic && (
          <div className="space-y-4 pl-4 border-l-2 border-muted">
            <div className="space-y-2">
              <Label htmlFor="publicName">Publikt namn (valfritt)</Label>
              <Input
                id="publicName"
                value={publicName}
                onChange={(e) => setPublicName(e.target.value)}
                placeholder="Om annat än kontonamn"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Titel</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="t.ex. Gårdshjälp, Försäljning"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bio">Kort beskrivning</Label>
              <Textarea
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Berätta lite om personen..."
                maxLength={240}
                rows={3}
              />
              <p className="text-sm text-muted-foreground">{bio.length}/240 tecken</p>
            </div>
            <p className="text-sm text-muted-foreground">
              Profilbild kan läggas till efter att personen accepterat inbjudan.
            </p>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-2 pt-4">
        <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSending}>
          Avbryt
        </Button>
        <Button onClick={handleSubmit} disabled={isSending}>
          {isSending ? 'Skickar...' : 'Skicka inbjudan'}
        </Button>
      </div>
    </ResponsiveOverlay>
  );
}
